import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Truck, ArrowRight, BookMarked } from "lucide-react";
import GlossaryContent from "@/components/GlossaryContent";
import { GLOSSARY } from "@/data/glossary";
import { applyPageSeo } from "@/lib/seo";

const PAGE_URL = "https://routehaulpathways.com/trucking-glossary";
const OG_IMAGE = "https://static.prod-images.emergentagent.com/jobs/ad103537-b988-4363-adb5-ef152c7e8f33/images/0f477aef3129ed94884ae2131216e231df216276672ff392182f33739154298d.jpeg";
const TITLE = `Trucking & Owner-Operator Glossary: ${GLOSSARY.length} Key Terms Explained | RouteHaul`;
const DESCRIPTION =
  "A plain-English trucking glossary for owner-operators: LLC, EIN, USDOT, MC number, BOC-3, UCR, IFTA, MVR, ELD, HOS, factoring, CSA score and more — every term you need to run your own authority, defined simply.";

export default function GlossaryPublic() {
  useEffect(() => applyPageSeo({
    title: TITLE,
    description: DESCRIPTION,
    canonical: PAGE_URL,
    image: OG_IMAGE,
    imageAlt: "Trucking glossary for owner-operators — RouteHaul Logistics Pathways",
    seoKey: "glossary",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "DefinedTermSet",
      name: "Trucking & Owner-Operator Glossary",
      description: DESCRIPTION,
      url: PAGE_URL,
      hasDefinedTerm: GLOSSARY.map((t) => ({
        "@type": "DefinedTerm",
        name: t.term,
        description: t.def,
      })),
    },
  }), []);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="glossary-public-page">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between gap-4">
          <Link to="/" data-testid="glossary-brand" className="flex items-center gap-2 font-heading font-black text-lg tracking-tight text-slate-900">
            <Truck className="w-6 h-6 text-brand" strokeWidth={2.5} />
            <span>RouteHaul<span className="hidden sm:inline text-slate-400 font-bold"> Logistics Pathways</span></span>
          </Link>
          <div className="flex items-center gap-2 sm:gap-3">
            <Link to="/login" data-testid="glossary-login"><Button variant="ghost" className="h-10 px-4 rounded-full text-slate-700 font-bold text-xs sm:text-sm tracking-wide uppercase hover:bg-slate-100">Log in</Button></Link>
            <Link to="/register" data-testid="glossary-header-signup"><Button className="h-10 px-4 sm:px-5 rounded-full bg-brand hover:bg-brand-hover text-white font-bold text-xs sm:text-sm tracking-wide uppercase">Get started</Button></Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-4xl mx-auto px-6 pt-14 pb-4">
        <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-brand mb-3">
            <BookMarked className="w-4 h-4" /> Key terms
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter font-black text-slate-900 leading-[1.02]">
            The owner-operator's trucking glossary
          </h1>
          <p className="mt-4 text-slate-600 max-w-2xl leading-relaxed">
            Every acronym and term you'll hit when starting your own trucking business, explained in plain
            English — <strong>LLC, EIN, USDOT, MC number, BOC-3, UCR, IFTA, MVR, ELD, HOS, factoring, CSA
            score</strong> and more. Search for anything you're unsure about.
          </p>
        </motion.div>
      </section>

      {/* Glossary (reuses the searchable component) */}
      <section className="max-w-4xl mx-auto px-6 pb-16">
        <GlossaryContent />
      </section>

      {/* CTA */}
      <section className="bg-brand text-white">
        <div className="max-w-3xl mx-auto px-6 py-14 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tighter font-black leading-tight">Ready to put these terms to work?</h2>
          <p className="mt-4 text-orange-50 text-lg">Create a free account to get the full 12-Step Business Foundation Guide — where each of these terms is explained inside the step where you'll actually use it.</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/register" data-testid="glossary-final-cta">
              <Button className="h-14 px-8 rounded-full bg-white text-brand hover:bg-orange-50 text-base font-bold group w-full sm:w-auto">
                Start the free guide
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <Link to="/how-to-get-your-own-authority" data-testid="glossary-authority-link">
              <Button variant="outline" className="h-14 px-8 rounded-full border-white/60 bg-transparent text-white hover:bg-white/10 text-base font-bold w-full sm:w-auto">
                How to get your authority
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-8 text-sm text-slate-400 flex items-center justify-between flex-wrap gap-2">
          <span>© {new Date().getFullYear()} RouteHaul Logistics Pathways</span>
          <div className="flex items-center gap-4">
            <Link to="/" className="hover:text-slate-900">Home</Link>
            <Link to="/how-to-get-your-own-authority" className="hover:text-slate-900">Get Your Authority</Link>
            <Link to="/how-much-do-owner-operators-make" className="hover:text-slate-900">Owner-Operator Pay</Link>
            <Link to="/cost-per-mile-calculator" className="hover:text-slate-900">CPM Calculator</Link>
            <Link to="/ifta-fuel-tax-calculator" className="hover:text-slate-900">IFTA Calculator</Link>
            <Link to="/owner-operator-faq" className="hover:text-slate-900">FAQ</Link>
            <Link to="/terms" className="hover:text-slate-900">Terms</Link>
            <Link to="/privacy" className="hover:text-slate-900">Privacy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
